import { useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, IconButton, Stack, Typography } from "@mui/material"
import { faTrashCan } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { enqueueSnackbar } from "notistack"
import { multiply } from "dinero.js"

import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useProductStock } from "../../../../productAvailability/useProductStock"
import { ProductCounter } from "../../shop/product/ProductCounter"

import type { TakeawayCartItem, TakeawayProduct } from "../takeawayTypes"

const useStyles = makeStyles()((theme) => ({
    button: {
        padding: 0,
        color: theme.palette.tailwind.slate[400],
        transition: "all 0.3s ease-out",
        "&:hover": {
            color: theme.palette.tailwind.red[400],
        },
    },
    counterWrapper: {
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        paddingRight: "2rem",
    },
    floatingDelete: {
        position: "absolute",
        right: "0",
        top: "50%",
        transform: "translateY(-50%)",
        opacity: 0,
        transition: "opacity 0.3s ease-in-out",
        "&.visible": {
            opacity: 1,
        },
    },
    badgePayroll: {
        padding: ".25rem .5rem",
        borderRadius: ".375rem",
        background: theme.palette.tailwind.slate[100],
        color: theme.palette.tailwind.slate[950],
        whiteSpace: "nowrap",
    },
    badgeDiscount: {
        padding: ".25rem .5rem",
        borderRadius: ".375rem",
        background: theme.palette.tailwind.slate[800],
        color: theme.palette.common.white,
        whiteSpace: "nowrap",
    },
}))

type TakeawayCartItemRowProps = Readonly<{
    item: TakeawayCartItem
    moneyFactory: ReturnType<typeof useMoney>
    onQuantityChange: (product: TakeawayProduct, quantity: number) => void
}>

export function TakeawayCartItemRow({ item, moneyFactory, onQuantityChange }: TakeawayCartItemRowProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const [isHovering, setIsHovering] = useState(false)
    const { product, quantity } = item
    const { disableIncrease } = useProductStock({ stock: product.stock, currentQuantity: quantity })

    const lineTotal = useMemo(
        () => (product.price ? multiply(toDinero(product.price), quantity) : null),
        [product.price, quantity],
    )

    const offerDiscount = product.offerDetails?.discount
    const discountLabel = useMemo(() => {
        if (!offerDiscount?.percentage) return null
        const pct = Math.round(offerDiscount.percentage.amount / 10 ** offerDiscount.percentage.scale)
        return l10n.getString("takeaway-checkout-discount-label", { percentage: pct })
    }, [offerDiscount, l10n])

    return (
        <Stack justifyContent="space-between" width="100%" gap=".5rem">
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap=".5rem">
                <Typography variant="body1">{product.name}</Typography>
            </Stack>

            {(product.paymentDetails?.method || discountLabel) && (
                <Stack direction="row" gap=".5rem" flexWrap="wrap">
                    {product.paymentDetails?.method && (
                        <LocalizedStrict
                            id="takeaway-checkout-payment-hint-by-method"
                            vars={{ method: product.paymentDetails.method.toLowerCase().replace(/_/g, " ") }}
                        >
                            <Typography variant="body1" component="span" className={classes.badgePayroll}>
                                Paid by {product.paymentDetails.method}
                            </Typography>
                        </LocalizedStrict>
                    )}
                    {discountLabel && (
                        <Typography variant="body1" component="span" className={classes.badgeDiscount}>
                            {discountLabel}
                        </Typography>
                    )}
                </Stack>
            )}

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-end"
                gap=".5rem"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
            >
                <Box className={classes.counterWrapper}>
                    <ProductCounter
                        count={quantity}
                        handleCounter={(qty) => onQuantityChange(product, qty)}
                        disableIncrease={disableIncrease}
                        onDisabledClick={() =>
                            enqueueSnackbar(l10n.getString("takeaway-product-sold-out-snackbar"), {
                                variant: "warning",
                            })
                        }
                    />
                    <IconButton
                        size="small"
                        onClick={() => onQuantityChange(product, 0)}
                        className={cx(classes.button, classes.floatingDelete, isHovering && "visible")}
                    >
                        <FontAwesomeIcon icon={faTrashCan} size="lg" />
                    </IconButton>
                </Box>

                {lineTotal && <Typography variant="subtitle1">{moneyFactory.formatAsNumber(lineTotal)}</Typography>}
            </Stack>
        </Stack>
    )
}
